import React from 'react'
import { useTranslation } from 'react-i18next'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import type { ColumnId } from '../lib/constants'
import PrintColumnsPicker from './PrintColumnsPicker'
import {
  ORIENTATIONS,
  PAPER_SIZES,
  PRINT_SCALES,
  PRINT_ZOOMS,
  type Orientation,
  type PaperSize,
  type PrintOptions,
  type PrintScale,
  type PrintZoom,
} from './print-options'

/** The boolean options; narrower than `keyof PrintOptions` so the toggle rows stay type-safe. */
type ToggleKey = 'includeBaseline' | 'includeLinks' | 'includeCritical' | 'includeLegend' | 'repeatColumns'

interface Props {
  options: PrintOptions
  onChange: (patch: Partial<PrintOptions>) => void
  hasBaseline: boolean
  /** Locked while a print or an export is in flight — the run uses the options it started with. */
  disabled?: boolean
  summary: string
  /** Columns chosen for this print (seeded from the screen by the dialog). */
  columns: ColumnId[]
  onToggleColumn: (id: ColumnId, checked: boolean) => void
  /** Number of timeline strips in the current layout — the repeat-columns switch only matters from two on. */
  stripCount: number
}

const zoomLabelKey = (z: PrintZoom): string => (z === 'fit' ? 'Schedule_print_zoom_fit' : `Schedule_zoom_${z}`)

/** The dialog's left column (spec C §5): paper, orientation, scale, timeline zoom, the content
 * toggles and the column picker. Every change is pushed straight back to the dialog, which persists it. */
const PrintOptionsForm: React.FC<Props> = ({ options, onChange, hasBaseline, disabled = false, summary, columns, onToggleColumn, stripCount }) => {
  const { t } = useTranslation()

  const toggles: { id: string; labelKey: string; key: ToggleKey; disabled: boolean }[] = [
    { id: 'print-baseline', labelKey: 'Schedule_print_include_baseline', key: 'includeBaseline', disabled: disabled || !hasBaseline },
    { id: 'print-links', labelKey: 'Schedule_print_include_links', key: 'includeLinks', disabled },
    { id: 'print-critical', labelKey: 'Schedule_print_include_critical', key: 'includeCritical', disabled },
    { id: 'print-legend', labelKey: 'Schedule_print_include_legend', key: 'includeLegend', disabled },
    { id: 'print-repeat-columns', labelKey: 'Schedule_print_repeat_columns', key: 'repeatColumns', disabled: disabled || stripCount < 2 },
  ]

  return (
    <div className="w-64 shrink-0 space-y-3">
      <div className="space-y-1">
        <Label htmlFor="print-paper" className="text-xs text-muted-foreground">{t('Schedule_print_paper')}</Label>
        <Select value={options.paper} disabled={disabled} onValueChange={(v) => onChange({ paper: v as PaperSize })}>
          <SelectTrigger id="print-paper" className="h-8 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            {PAPER_SIZES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1">
        <Label htmlFor="print-orientation" className="text-xs text-muted-foreground">{t('Schedule_print_orientation')}</Label>
        <Select value={options.orientation} disabled={disabled} onValueChange={(v) => onChange({ orientation: v as Orientation })}>
          <SelectTrigger id="print-orientation" className="h-8 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            {ORIENTATIONS.map((o) => (
              <SelectItem key={o} value={o}>{t(o === 'landscape' ? 'Schedule_print_landscape' : 'Schedule_print_portrait')}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1">
        <Label htmlFor="print-scale" className="text-xs text-muted-foreground">{t('Schedule_print_scale')}</Label>
        <Select value={String(options.scale)} disabled={disabled} onValueChange={(v) => onChange({ scale: Number(v) as PrintScale })}>
          <SelectTrigger id="print-scale" className="h-8 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            {PRINT_SCALES.map((s) => <SelectItem key={s} value={String(s)}>{`${Math.round(s * 100)} %`}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1">
        <Label htmlFor="print-zoom" className="text-xs text-muted-foreground">{t('Schedule_print_zoom')}</Label>
        <Select value={options.zoom} disabled={disabled} onValueChange={(v) => onChange({ zoom: v as PrintZoom })}>
          <SelectTrigger id="print-zoom" className="h-8 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            {PRINT_ZOOMS.map((z) => <SelectItem key={z} value={z}>{t(zoomLabelKey(z))}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {toggles.map((row) => (
        <div key={row.id} className="flex items-center justify-between">
          <Label htmlFor={row.id} className="text-sm font-normal">{t(row.labelKey)}</Label>
          <Switch
            id={row.id}
            checked={options[row.key] && (row.key !== 'includeBaseline' || hasBaseline)}
            disabled={row.disabled}
            onCheckedChange={(checked) => onChange({ [row.key]: checked })}
          />
        </div>
      ))}

      <PrintColumnsPicker selected={columns} onToggle={onToggleColumn} disabled={disabled} />

      <p className="text-xs text-muted-foreground">{summary}</p>
    </div>
  )
}

export default PrintOptionsForm
